/**
 * The landing page's illustration: a small cartographer robot uses a lens to
 * reveal the layered tensor map inside an otherwise opaque model.
 *
 * This is deliberately an illustration rather than a miniature anatomy map.
 * A baked chart would look like a real measurement even when its numbers were
 * invented; this scene is unmistakably a metaphor. It keeps the lens that gives
 * TensorLens its name, but adds a curious protagonist and a moment of discovery
 * so the empty state feels like an invitation rather than an instrument diagram.
 *
 * The slightly uneven paths, repeated outlines and construction marks provide
 * the character of a pencil sketch without using a raster texture or an SVG
 * filter. Colour comes only from the application's role-based tokens, so the
 * drawing re-themes with the rest of the page.
 */

/**
 * The illustration is one semantic image. Its title describes what the scene
 * means rather than enumerating decorative geometry.
 */
export function discoverySvg(): string {
  return `<svg class="discovery" viewBox="0 0 440 190" role="img" aria-labelledby="discovery-title" focusable="false">
    <title id="discovery-title">A curious little robot uses a hand-drawn lens to reveal a map of layered tensors inside an opaque model.</title>
    <defs>
      <clipPath id="discovery-lens-clip">
        <circle cx="202" cy="91" r="42"/>
      </clipPath>
    </defs>

    <!-- Loose map marks make the scene feel explored rather than engineered. -->
    <g class="discovery-construction" fill="none" aria-hidden="true">
      <path d="M18 158 C67 173 112 160 145 146 S223 151 267 139 S352 154 421 137"/>
      <path d="M32 35 l4 8 8 3-8 3-4 8-3-8-8-3 8-3z"/>
      <path d="M404 23 l2.5 5 5 2-5 2-2.5 5-2-5-5-2 5-2z"/>
      <path d="M126 29 c6-7 13-8 19-3 M129 35 c6-5 12-5 17-1"/>
      <circle cx="20" cy="158" r="2.5"/>
      <circle cx="267" cy="139" r="2.5"/>
      <circle cx="421" cy="137" r="2.5"/>
    </g>

    <!-- The opaque model sits behind the lens; only the lens exposes its grid. -->
    <g class="discovery-model">
      <path class="discovery-pencil-echo" d="M220 43 Q238 37 259 45 L264 132 Q244 141 219 134 Z"/>
      <path class="discovery-model-shell" d="M220 43 Q238 37 259 45 L264 132 Q244 141 219 134 Z"/>
      <path class="discovery-model-hatch" d="M246 48 l10 7 M248 61 l10 7 M249 75 l10 7 M250 89 l10 7 M251 103 l9 7 M250 118 l8 6"/>
    </g>

    <!-- Through the lens the same object becomes a small, legible tensor map. -->
    <g clip-path="url(#discovery-lens-clip)" aria-hidden="true">
      <path class="discovery-lens-window" d="M214 42 H266 V137 H214 Z"/>
      <path class="discovery-grid-line" d="M214 58 H264 M214 75 H264 M214 92 H264 M214 109 H264 M214 126 H264 M231 42 V137 M247 42 V137"/>
      <path class="discovery-grid-accent" d="M216 77 H229 V90 H216 Z M233 94 H245 V107 H233 Z M249 60 H262 V73 H249 Z"/>
    </g>

    <!-- The model opens into separate tensor layers on the far side. -->
    <g class="discovery-tensor-map" aria-hidden="true">
      <path class="discovery-connector" d="M260 54 C272 53 277 50 288 47 M263 72 C273 70 279 68 288 66 M264 91 H285 M263 111 C274 113 280 115 289 117 M260 128 C272 133 279 137 290 141"/>

      <g class="discovery-layer" style="--layer-index:0">
        <path class="discovery-layer-echo" d="M286 42 L379 31 L414 43 L316 56 Z"/>
        <path class="discovery-layer-sheet discovery-layer-sheet--one" d="M286 42 L379 31 L414 43 L316 56 Z"/>
        <path class="discovery-layer-detail" d="M310 43 l23-3 M340 39 l35-4 M382 36 l11 4"/>
      </g>
      <g class="discovery-layer" style="--layer-index:1">
        <path class="discovery-layer-echo" d="M281 64 L378 51 L409 64 L309 78 Z"/>
        <path class="discovery-layer-sheet discovery-layer-sheet--two" d="M281 64 L378 51 L409 64 L309 78 Z"/>
        <path class="discovery-layer-detail" d="M306 65 l28-4 M341 60 l23-3 M372 56 l15 6"/>
      </g>
      <g class="discovery-layer" style="--layer-index:2">
        <path class="discovery-layer-echo discovery-layer-echo--accent" d="M278 88 L371 75 L404 89 L306 103 Z"/>
        <path class="discovery-layer-sheet discovery-layer-sheet--focus" d="M278 88 L371 75 L404 89 L306 103 Z"/>
        <path class="discovery-layer-detail discovery-layer-detail--focus" d="M303 89 l17-3 M326 85 l30-4 M363 80 l19 7"/>
        <circle class="discovery-layer-node" cx="391" cy="89" r="3.5"/>
      </g>
      <g class="discovery-layer" style="--layer-index:3">
        <path class="discovery-layer-echo" d="M282 113 L377 99 L407 113 L310 127 Z"/>
        <path class="discovery-layer-sheet discovery-layer-sheet--three" d="M282 113 L377 99 L407 113 L310 127 Z"/>
        <path class="discovery-layer-detail" d="M307 114 l25-4 M340 109 l36-5 M383 105 l9 4"/>
      </g>
      <g class="discovery-layer" style="--layer-index:4">
        <path class="discovery-layer-echo" d="M291 138 L382 124 L413 137 L318 153 Z"/>
        <path class="discovery-layer-sheet discovery-layer-sheet--four" d="M291 138 L382 124 L413 137 L318 153 Z"/>
        <path class="discovery-layer-detail" d="M317 139 l18-3 M342 135 l29-5 M378 129 l15 6"/>
      </g>
    </g>

    <!-- A round, friendly cartographer holds the lens by its handle. -->
    <g class="discovery-robot" aria-hidden="true">
      <path class="discovery-robot-shadow" d="M33 158 C54 163 91 163 116 157"/>
      <path class="discovery-pencil-echo" d="M49 108 C48 96 57 88 75 87 C94 86 105 96 105 111 L103 143 C90 152 63 152 50 143 Z"/>
      <path class="discovery-robot-body" d="M49 108 C48 96 57 88 75 87 C94 86 105 96 105 111 L103 143 C90 152 63 152 50 143 Z"/>
      <path class="discovery-robot-panel" d="M62 118 Q76 113 91 118 L90 136 Q76 141 62 136 Z"/>
      <path class="discovery-robot-panel-mark" d="M68 126 h17 M72 131 h9"/>

      <path class="discovery-antenna" d="M76 53 C74 46 70 41 66 37"/>
      <circle class="discovery-antenna-tip" cx="65" cy="35" r="4"/>
      <path class="discovery-pencil-echo" d="M43 70 C46 51 60 43 78 44 C99 45 111 59 109 78 C107 94 94 103 76 102 C57 101 42 91 43 70 Z"/>
      <path class="discovery-robot-head" d="M43 70 C46 51 60 43 78 44 C99 45 111 59 109 78 C107 94 94 103 76 102 C57 101 42 91 43 70 Z"/>
      <path class="discovery-robot-face" d="M58 67 Q75 60 94 66 L93 82 Q76 89 57 81 Z"/>
      <circle class="discovery-robot-eye" cx="68" cy="73" r="3.3"/>
      <path class="discovery-robot-eye discovery-robot-eye--wink" d="M84 72 q4 3 8 0"/>
      <path class="discovery-robot-smile" d="M72 83 q7 5 14-1"/>

      <path class="discovery-robot-arm" d="M103 109 C125 107 137 96 150 86"/>
      <path class="discovery-robot-arm-echo" d="M104 111 C126 109 139 98 152 88"/>
      <circle class="discovery-robot-hand" cx="153" cy="85" r="6"/>
      <circle class="discovery-robot-wheel" cx="62" cy="150" r="8"/>
      <circle class="discovery-robot-wheel" cx="94" cy="150" r="8"/>
      <path class="discovery-wheel-mark" d="M58 150 h8 M90 150 h8"/>
    </g>

    <!-- The intentionally imperfect double contour is the drawing's focal mark. -->
    <g class="discovery-lens" aria-hidden="true">
      <path class="discovery-lens-handle-echo" d="M169 122 L149 148"/>
      <path class="discovery-lens-handle" d="M170 121 L150 147"/>
      <circle class="discovery-lens-fill" cx="202" cy="91" r="42"/>
      <path class="discovery-lens-echo" d="M202 48 C225 48 244 67 244 91 C244 115 226 133 201 133 C177 133 159 115 160 91 C161 67 179 49 202 48 Z"/>
      <path class="discovery-lens-ring" d="M201 49 C225 48 244 67 244 91 C244 115 226 133 201 133 C177 133 159 115 160 91 C161 67 179 50 201 49 Z"/>
      <path class="discovery-lens-glint" d="M174 72 C179 63 187 58 196 56"/>
      <circle class="discovery-lens-rivet" cx="170" cy="121" r="3"/>
    </g>
  </svg>`;
}
